import { useMemo } from 'react';
import { ValueLabelOption } from '@elementary/common-ui/types';
import { useQuery } from '@tanstack/react-query';
import { DESTINATION_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import * as api from 'shared/services/api';
import {
  EmailDestinationOptions,
  IncidentIoDestinationOptions,
  IntegrationType,
  MicrosoftTeamsBotDestinationOptions,
  MicrosoftTeamsDestinationOptions,
  OpsgenieDestinationOptions,
  PagerDutyDestinationOptions,
  RootlyDestinationOptions,
  SlackDestinationOptions,
} from 'shared/types';

const useAlertDestinations = () => {
  const {
    data: destinationOptionsData,
    isError,
    isLoading,
  } = useQuery([DESTINATION_OPTIONS], () => api.fetchDestinationOptions());

  const alertDestinationOptions = useMemo(() => {
    const slackData = destinationOptionsData?.find(
      (dest) => dest.type === 'slack',
    ) as SlackDestinationOptions;

    const teamsData = destinationOptionsData?.find(
      (dest) => dest.type === 'microsoft_teams',
    ) as MicrosoftTeamsDestinationOptions;

    const teamsBotData = destinationOptionsData?.find(
      (dest) => dest.type === 'microsoft_teams_bot',
    ) as MicrosoftTeamsBotDestinationOptions;

    const pagerDutyData = destinationOptionsData?.find(
      (dest) => dest.type === 'pager_duty',
    ) as PagerDutyDestinationOptions;

    const opsgenieData = destinationOptionsData?.find(
      (dest) => dest.type === 'opsgenie',
    ) as OpsgenieDestinationOptions;

    const incidentIoData = destinationOptionsData?.find(
      (dest) => dest.type === 'incident_io',
    ) as IncidentIoDestinationOptions;

    const rootlyData = destinationOptionsData?.find(
      (dest) => dest.type === 'rootly',
    ) as RootlyDestinationOptions;

    const emailData = destinationOptionsData?.find(
      (dest) => dest.type === 'email',
    ) as EmailDestinationOptions;

    const options: Record<
      IntegrationType,
      { id: string; options: ValueLabelOption[]; isLoaded: boolean }
    > = {
      slack: {
        id: slackData?.id,
        options:
          slackData?.channels?.map((c) => ({
            label: c,
            value: c,
          })) || [],
        isLoaded: slackData?.is_list_full,
      },

      microsoft_teams: {
        id: teamsData?.id,
        options:
          teamsData?.webhooks?.map((w) => ({
            label: w,
            value: w,
          })) || [],
        isLoaded: true,
      },

      microsoft_teams_bot: {
        id: teamsBotData?.id,
        options:
          teamsBotData?.channel_options?.map((option) => ({
            label: option.label,
            value: option.value,
          })) ?? [],
        isLoaded: true,
      },

      pager_duty: {
        id: pagerDutyData?.id,
        options:
          pagerDutyData?.services?.map((s) => ({
            label: s.name,
            value: s.id,
          })) || [],
        isLoaded: true,
      },

      opsgenie: {
        id: opsgenieData?.id,
        options: [],
        isLoaded: false,
      },

      incident_io: {
        id: incidentIoData?.id,
        options:
          incidentIoData?.sources?.map((s) => ({
            label: s,
            value: s,
          })) || [],
        isLoaded: true,
      },

      rootly: {
        id: rootlyData?.id,
        options:
          rootlyData?.sources?.map((s) => ({
            label: s,
            value: s,
          })) || [],
        isLoaded: true,
      },

      email: {
        id: emailData?.id,
        options:
          emailData?.emails?.map((c) => ({
            label: c,
            value: c,
          })) || [],
        isLoaded: true,
      },
    };

    return options;
  }, [destinationOptionsData]);

  const alertProviders = destinationOptionsData?.map((dest) => dest.type);

  const getDestinationItem = (
    type: IntegrationType,
    id: string,
  ): ValueLabelOption | ValueLabelOption[] => {
    if (type === 'email') {
      try {
        const parsed = JSON.parse(id);
        if (parsed.to && Array.isArray(parsed.to)) {
          return parsed.to.map((email: string) => ({
            label: email,
            value: email,
          }));
        }
      } catch {
        return { label: id, value: id };
      }
    }

    return (
      alertDestinationOptions[type]?.options.find((o) => o.value === id) || {
        label: id,
        value: id,
      }
    );
  };

  return {
    alertDestinationOptions,
    alertProviders,
    getDestinationItem,
    alertDestinationsError: isError,
    alertDestinationsLoading: isLoading,
  };
};

export default useAlertDestinations;
